import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, ChevronLeft, ChevronRight, Flag, Loader2, CheckCircle2, Send } from "lucide-react";

export default function ExamAttempt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  useEffect(() => {
    api.get(`/attempts/${id}`).then(({ data }) => {
      if (data.status !== "in_progress") {
        navigate(`/app/results/${id}`);
        return;
      }
      setAttempt(data);
      setAnswers(data.answers || {});
      const end = new Date(data.started_at).getTime() + data.duration_minutes * 60000;
      setTimeLeft(Math.max(0, Math.floor((end - Date.now()) / 1000)));
    }).catch(() => navigate("/app/exams"));
  }, [id, navigate]);

  const doSubmit = useCallback(async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const payload = { answers: Object.entries(answers).map(([qid, a]) => ({ question_id: qid, selected: a.selected || [], text: a.text || "" })) };
      const { data } = await api.post(`/attempts/${id}/submit`, payload);
      toast.success(auto ? "Time up — exam auto-submitted" : "Exam submitted");
      if (data.has_subjective) navigate("/app/exams");
      else navigate(`/app/results/${id}`);
    } catch (e) {
      submittedRef.current = false;
      toast.error("Submit failed, retrying…");
      setSubmitting(false);
    }
  }, [answers, id, navigate]);

  // timer
  useEffect(() => {
    if (timeLeft == null) return;
    if (timeLeft <= 0) { doSubmit(true); return; }
    const t = setInterval(() => setTimeLeft((x) => (x != null ? x - 1 : x)), 1000);
    return () => clearInterval(t);
  }, [timeLeft, doSubmit]);

  // autosave
  useEffect(() => {
    if (!attempt) return;
    const t = setInterval(() => {
      api.post(`/attempts/${id}/save`, {
        answers: Object.entries(answers).map(([qid, a]) => ({ question_id: qid, selected: a.selected || [], text: a.text || "" })),
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [answers, attempt, id]);

  if (!attempt) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const q = attempt.questions[current];
  const ans = answers[q.id] || { selected: [], text: "" };
  const answeredCount = attempt.questions.filter((qq) => {
    const a = answers[qq.id];
    return a && ((a.selected && a.selected.length) || (a.text && a.text.trim()));
  }).length;

  const setSelected = (sel) => setAnswers({ ...answers, [q.id]: { ...ans, selected: sel } });
  const setText = (text) => setAnswers({ ...answers, [q.id]: { ...ans, text } });

  const toggleOption = (i) => {
    if (q.type === "mcq") setSelected([i]);
    else {
      const has = ans.selected.includes(i);
      setSelected(has ? ans.selected.filter((x) => x !== i) : [...ans.selected, i]);
    }
  };

  const mm = String(Math.floor((timeLeft || 0) / 60)).padStart(2, "0");
  const sl = String((timeLeft || 0) % 60).padStart(2, "0");
  const low = timeLeft != null && timeLeft < 60;

  return (
    <div className="min-h-screen bg-secondary/30 flex flex-col">
      {/* header */}
      <header className="h-16 bg-card border-b border-border flex items-center px-4 md:px-8 sticky top-0 z-30">
        <Brand iconOnly className="md:hidden" />
        <div className="hidden md:block">
          <div className="font-semibold leading-tight">{attempt.exam_title}</div>
          <div className="text-xs text-muted-foreground">{attempt.subject}</div>
        </div>
        <div className={`ml-auto flex items-center gap-2 px-4 py-2 font-mono text-lg font-semibold border ${low ? "border-destructive text-destructive animate-pulse" : "border-border"}`} data-testid="exam-timer">
          <Clock className="h-5 w-5" /> {mm}:{sl}
        </div>
        <Button className="ml-3 rounded-sm" onClick={() => setShowSubmit(true)} data-testid="submit-exam-btn">
          <Send className="h-4 w-4 mr-1.5" /> Submit
        </Button>
      </header>

      <div className="flex-1 max-w-6xl w-full mx-auto p-4 md:p-8 grid lg:grid-cols-[1fr_260px] gap-6">
        {/* question */}
        <div className="bg-card border border-border p-6 md:p-8" data-testid="question-panel">
          <div className="flex items-center gap-2 text-xs mb-4">
            <span className="font-mono text-muted-foreground">Question {current + 1} of {attempt.questions.length}</span>
            <span className="px-1.5 py-0.5 bg-accent text-accent-foreground uppercase">{q.type}</span>
            <span className="font-mono text-muted-foreground ml-auto">{q.marks} marks</span>
          </div>
          <p className="text-lg font-medium leading-relaxed">{q.text}</p>

          {["mcq", "msq"].includes(q.type) ? (
            <div className="mt-6 space-y-2">
              {q.options.map((o, i) => {
                const selected = ans.selected.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() => toggleOption(i)}
                    data-testid={`option-${i}`}
                    className={`w-full text-left flex items-center gap-3 p-4 border transition-colors ${selected ? "border-primary bg-accent" : "border-border hover:bg-secondary/50"}`}
                  >
                    <span className={`h-5 w-5 flex items-center justify-center border ${q.type === "mcq" ? "rounded-full" : ""} ${selected ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground"}`}>
                      {selected && <CheckCircle2 className="h-3.5 w-3.5" />}
                    </span>
                    <span>{o}</span>
                  </button>
                );
              })}
              {q.type === "msq" && <p className="text-xs text-muted-foreground">Select all correct answers.</p>}
            </div>
          ) : (
            <div className="mt-6">
              <Textarea
                value={ans.text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your answer here…"
                rows={q.type === "short" ? 4 : 10}
                data-testid="subjective-answer"
              />
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="outline" className="rounded-sm" disabled={current === 0} onClick={() => setCurrent(current - 1)} data-testid="prev-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <Button className="rounded-sm" disabled={current === attempt.questions.length - 1} onClick={() => setCurrent(current + 1)} data-testid="next-btn">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* navigator */}
        <aside className="bg-card border border-border p-5 h-fit lg:sticky lg:top-24">
          <div className="flex items-center justify-between mb-3">
            <span className="overline">Navigator</span>
            <span className="text-xs font-mono text-muted-foreground">{answeredCount}/{attempt.questions.length}</span>
          </div>
          <div className="grid grid-cols-6 lg:grid-cols-5 gap-2">
            {attempt.questions.map((qq, i) => {
              const a = answers[qq.id];
              const done = a && ((a.selected && a.selected.length) || (a.text && a.text.trim()));
              return (
                <button
                  key={qq.id}
                  onClick={() => setCurrent(i)}
                  data-testid={`nav-q-${i}`}
                  className={`aspect-square text-sm font-mono border transition-colors ${i === current ? "border-primary ring-1 ring-primary" : "border-border"} ${done ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-2"><span className="h-3 w-3 bg-primary inline-block" /> Answered</div>
            <div className="flex items-center gap-2"><span className="h-3 w-3 border border-border inline-block" /> Not answered</div>
          </div>
        </aside>
      </div>

      <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submit examination?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            You have answered <span className="font-semibold text-foreground">{answeredCount}</span> of {attempt.questions.length} questions.
            You cannot make changes after submitting.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmit(false)}>Keep working</Button>
            <Button onClick={() => doSubmit(false)} disabled={submitting} className="rounded-sm" data-testid="confirm-submit">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
